import React from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { Event } from '../../_graphql/generated/types';

interface SidebarProps {
  onClose?: () => void;
  events?: Event[];
  currentEventId?: string;
  onEventChange?: (eventId: string) => void;
  currentTab?: 'guests' | 'invitations' | 'analytics';
  isOnDashboard?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ onClose, events = [], currentEventId, onEventChange, currentTab, isOnDashboard = false }) => {
  const { logout } = useKindeAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentEvent = events.find(e => e.id === currentEventId);
  
  // Default to 'guests' tab if we're on an event page but no tab is specified
  const activeTab = currentTab || (!isOnDashboard && currentEvent ? 'guests' : undefined);
  return (
    <aside className="bg-white h-screen w-72 shadow-xl flex flex-col p-6 font-sans border-r border-gray-200">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
            <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              InviteWith<span className="text-rose-500">Love</span>
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">Event Management</p>
          </div>
        </div>
        {/* Close button for mobile */}
        <button
          onClick={onClose}
          className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-all"
          aria-label="Close menu"
        >
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Main navigation */}
      <nav className="flex-1">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Main Menu</div>
        <ul className="space-y-1">
          {/* All Events link */}
          <li>
            <a 
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-decoration-none font-medium transition-all ${
                location.pathname === '/app/events'
                  ? 'bg-gradient-to-r from-rose-400 to-pink-500 text-white shadow-md' 
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`} 
              href="/app/events"
              onClick={(e) => {
                e.preventDefault();
                navigate('/app/events');
              }}
            >
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span>My Events</span>
            </a>
          </li>
        </ul>

        {/* Current Event section - only show when viewing a specific event */}
        {events.length > 0 && currentEvent && !isOnDashboard && (
          <>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 mt-8">Current Event</div>
            
            {/* Event Selector */}
            <div className="mb-4">
              <select
                value={currentEventId || ''}
                onChange={(e) => onEventChange?.(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-400 text-sm transition-all"
              >
                {events.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.eventName}
                  </option>
                ))}
              </select>
            </div>

            <ul className="space-y-1">
              <li>
                <a className={`flex items-center gap-3 px-4 py-3 rounded-xl text-decoration-none font-medium transition-all ${
                  activeTab === 'guests' 
                    ? 'bg-gradient-to-r from-rose-400 to-pink-500 text-white shadow-md' 
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`} 
                href={currentEvent ? `/app/events/${currentEvent.code}/${currentEvent.eventName.toLowerCase().replace(/\s+/g, '-')}?tab=guests` : '#'}
                onClick={(e) => {
                  if (currentEvent) {
                    e.preventDefault();
                    navigate(`/app/events/${currentEvent.code}/${currentEvent.eventName.toLowerCase().replace(/\s+/g, '-')}?tab=guests`);
                  }
                }}>
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span>Guests</span>
                </a>
              </li>
              <li>
                <a className={`flex items-center gap-3 px-4 py-3 rounded-xl text-decoration-none font-medium transition-all ${
                  activeTab === 'invitations' 
                    ? 'bg-gradient-to-r from-rose-400 to-pink-500 text-white shadow-md' 
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`} 
                href={currentEvent ? `/app/events/${currentEvent.code}/${currentEvent.eventName.toLowerCase().replace(/\s+/g, '-')}?tab=invitations` : '#'}
                onClick={(e) => {
                  if (currentEvent) {
                    e.preventDefault();
                    navigate(`/app/events/${currentEvent.code}/${currentEvent.eventName.toLowerCase().replace(/\s+/g, '-')}?tab=invitations`);
                  }
                }}>
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Invitation Types</span>
                </a>
              </li>
              <li>
                <a className={`flex items-center gap-3 px-4 py-3 rounded-xl text-decoration-none font-medium transition-all ${
                  activeTab === 'analytics' 
                    ? 'bg-gradient-to-r from-rose-400 to-pink-500 text-white shadow-md' 
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`} 
                href={currentEvent ? `/app/events/${currentEvent.code}/${currentEvent.eventName.toLowerCase().replace(/\s+/g, '-')}?tab=analytics` : '#'}
                onClick={(e) => {
                  if (currentEvent) {
                    e.preventDefault();
                    navigate(`/app/events/${currentEvent.code}/${currentEvent.eventName.toLowerCase().replace(/\s+/g, '-')}?tab=analytics`);
                  }
                }}>
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span>Analytics</span>
                </a>
              </li>
            </ul>
          </>
        )}

      </nav>

      {/* Bottom section - Logout */}
      <div className="mt-auto space-y-4">
        {/* Logout button */}
        <button
          onClick={() => logout()}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 font-medium hover:bg-gray-100 hover:text-gray-900 transition-all border border-gray-200"
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;